<template>
  <div class="cc-chat-panel">
    <div class="cc-chat-panel-messages" ref="messagesContainer">
      <template v-for="(message, index) in messages">
        <system-message v-if="message.role === 'system'" :key="'s-' + index" :text="message.text" :content="message.content" :timestamp="message.timestamp" :is-last="index === messages.length - 1" :streaming="!!message.streaming" @action="handleAction($event, index)" />
        <user-message v-else-if="message.role === 'user'" :key="'u-' + index" :text="message.text" :initials="userInitials" :timestamp="message.timestamp" />
      </template>
    </div>
    <chat-input-bar
      ref="inputBar"
      :loading="loading"
      :locked="context.step === 'done'"
      locked-text="Chat closed. Start a new chat to make another template."
      @send="handleSend"
    />
  </div>
</template>

<script>
import ChatInputBar from '../../common/chat-panel/ChatInputBar.vue'
import SystemMessage from '../../common/chat-panel/SystemMessage.vue'
import UserMessage from '../../common/chat-panel/UserMessage.vue'
import api from '../../../../utils/api'
import { getAccessToken } from '../../../../utils/auth'
import templateMediaUpload from '../templateMediaUpload'
import { TEMPLATE_CHAT_PURPOSE, TEMPLATE_STEP_NUMBER } from '../constants'

// The AI Nudge Creation chat (server: controllers/nudge-console/templateChat.js). Same shape
// as the Create Campaign chat panel: the server is stateless, so the whole context goes up
// every turn; the session and its turns are kept in ai_console_chat so a draft resumes after
// a refresh or from the Dashboard's Draft Chats. The server opens the conversation itself.

// The statistics service can hand is_completed back as a string.
const isChatIncomplete = chat => {
  const v = chat && chat.is_completed
  return v !== true && v !== 'true' && v !== 1 && v !== '1' && v !== 't'
}

export default {
  name: 'TemplateChatPanel',
  components: { ChatInputBar, SystemMessage, UserMessage },
  mixins: [templateMediaUpload],
  props: {
    resumeChatId: { type: [String, Number], default: null }
  },
  emits: ['summary-change'],
  data () {
    const username = localStorage.getItem('username') || ''
    const initials = username.split('.').filter(p => p.length > 0).map(p => p[0].toUpperCase()).slice(0, 2).join('')
    return {
      loading: false,
      userInitials: initials || 'U',
      userId: username,
      chatId: null,
      messageSequence: 0,
      messages: [],
      context: {}
    }
  },
  watch: {
    messages () {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  },
  async mounted () {
    await this.initChat()
    this.emitSummary()
    this._autosaveTimer = setInterval(() => {
      if (this.context.step && this.context.step !== 'done') this.persistState()
    }, 60000)
  },
  beforeDestroy () {
    if (this._autosaveTimer) clearInterval(this._autosaveTimer)
  },
  methods: {
    async trackEvent (eventName, properties = {}) {
      try {
        await api.buildRequest('ai-console-events', 'post', {
          event_name: eventName,
          user_id: this.userId,
          ai_console_chat_id: this.chatId,
          instance: 'nudge-console-create-template',
          properties
        })
      } catch (err) { /* analytics only */ }
    },

    async initChat () {
      if (this.resumeChatId) {
        try {
          const { data } = await api.buildRequest(`ai-console-chat/${this.resumeChatId}`, 'get')
          const payload = data && data.payload
          const session = Array.isArray(payload) ? payload[0] : payload
          if (session && session.id && session.purpose === TEMPLATE_CHAT_PURPOSE && isChatIncomplete(session)) {
            await this.resumeSession(session)
            return
          }
        } catch (err) { /* stale link: fall through */ }
      }
      try {
        const { data } = await api.buildRequest('ai-console-chat', 'get', { user_id: this.userId })
        const list = data && data.payload && (Array.isArray(data.payload) ? data.payload : [data.payload])
        // Newest first: resume the latest unfinished template chat, if it's the latest template chat.
        const last = (list || []).find(c => c && c.purpose === TEMPLATE_CHAT_PURPOSE)
        if (last && isChatIncomplete(last)) {
          await this.resumeSession(last)
          return
        }
      } catch (err) { /* start a new one */ }
      await this.createChat()
    },

    async resumeSession (session) {
      this.chatId = session.id
      const saved = session.summary || {}
      this.context = saved.context || {}
      await this.restoreHistory()
      if (!this.messages.length) await this.sendToBackend({})
    },

    async createChat () {
      try {
        const { data } = await api.buildRequest('ai-console-chat', 'post', { user_id: this.userId, purpose: TEMPLATE_CHAT_PURPOSE })
        const session = data && data.payload
        if (session && session.id) {
          if (data.resumed) {
            await this.resumeSession(session)
            return
          }
          this.chatId = session.id
          this.trackEvent('template_creation_started', { mode: 'template' })
        }
      } catch (err) {
        console.error('Failed to create chat session', err)
      }
      await this.sendToBackend({})
    },

    async restoreHistory () {
      try {
        const { data } = await api.buildRequest(`ai-console-chat/${this.chatId}/history`, 'get')
        const history = data && data.payload
        if (!Array.isArray(history) || !history.length) return
        this.messageSequence = history[history.length - 1].message_sequence
        this.messages = history.map(turn => {
          const isBot = turn.sender === 'bot'
          let content = null
          let text = turn.message
          if (isBot && turn.message) {
            try {
              const parsed = JSON.parse(turn.message)
              if (Array.isArray(parsed)) { content = parsed; text = null }
            } catch (_) {}
          }
          return { role: isBot ? 'system' : 'user', text, content, timestamp: turn.created_at ? new Date(turn.created_at).getTime() : Date.now() }
        })
      } catch (err) {
        console.error('Failed to restore chat history', err)
      }
    },

    // Called before "+ New Chat" remounts the panel, so the new one doesn't resume this one.
    async abandonChat () {
      if (!this.chatId) return
      try {
        await api.buildRequest(`ai-console-chat/${this.chatId}`, 'put', { is_completed: true })
      } catch (err) {
        console.error('Failed to abandon chat session', err)
      }
    },

    async saveTurn (sender, receiver, message) {
      if (!this.chatId) return
      this.messageSequence += 1
      try {
        await api.buildRequest(`ai-console-chat/${this.chatId}`, 'put', { turn: { sender, receiver, message, message_sequence: this.messageSequence } })
      } catch (err) {
        console.error('Failed to save turn', err)
      }
    },

    // mode: 'template' marks the session as a template draft for the Dashboard's Draft Chats.
    async persistState () {
      if (!this.chatId) return
      try {
        await api.buildRequest(`ai-console-chat/${this.chatId}`, 'put', {
          summary: { mode: 'template', context: this.context },
          is_completed: this.context.step === 'done'
        })
      } catch (err) {
        console.error('Failed to persist chat state', err)
      }
    },

    appendUserMessage (text) {
      this.messages.push({ role: 'user', text, timestamp: Date.now() })
      this.saveTurn(this.userId, 'bot', text)
    },

    resolveDisplayText (messageIndex, action) {
      const { type, value, elementIndex } = action
      const block = this.messages[messageIndex] && this.messages[messageIndex].content && this.messages[messageIndex].content[elementIndex]
      if (type === 'chip-multi-confirm') return Array.isArray(value) ? value.join(', ') : String(value)
      if (type === 'card-confirm' && block && Array.isArray(block.items)) {
        const card = block.items[value]
        return card ? card.title : String(value)
      }
      if ((type === 'chip' || type === 'button') && block && Array.isArray(block.items)) {
        const item = block.items.find(it => (typeof it === 'string' ? it : it.value) === value)
        if (item) return typeof item === 'string' ? item : item.label
      }
      if (type === 'content-confirm') return 'Check and continue'
      if (type === 'content-regenerate') return value && String(value).trim() ? `Regenerate: ${String(value).trim()}` : 'Regenerate'
      if (type === 'form') {
        const vals = Object.values(value || {}).filter(v => v !== '' && v != null)
        return vals.length ? vals.join(', ') : ''
      }
      return value === undefined || value === null ? '' : String(value)
    },

    handleAction (action, messageIndex) {
      if (this.loading) return
      if (action.type === 'upload') {
        const block = ((this.messages[messageIndex] || {}).content || [])[action.elementIndex] || {}
        // Upload boxes stay clickable in older messages; only the live media step takes one.
        if (!block.upload || block.upload.kind !== 'template-media' || messageIndex !== this.messages.length - 1 || this.context.step !== 'media') return
        this.appendUserMessage(action.value && action.value.name ? action.value.name : 'Uploaded file')
        this.uploadTemplateMedia(action.value, block.upload)
        return
      }
      this.appendUserMessage(this.resolveDisplayText(messageIndex, action))
      if (action.value === 'submit') this.trackEvent('template_submit_clicked')
      this.sendToBackend({ action })
    },

    handleSend (text) {
      if (!text || this.loading) return
      this.appendUserMessage(text)
      this.sendToBackend({ message: text })
    },

    async sendToBackend ({ action = null, message = null } = {}) {
      this.loading = true
      const streamIdx = this.messages.length
      this.messages.push({ role: 'system', content: null, timestamp: Date.now(), streaming: true })
      try {
        const host = window.location.hostname + (window.location.hostname === 'localhost' ? ':8080' : '')
        const response = await fetch(`${window.location.protocol}//${host}/api/nudge-template-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}`, 'X-User-Email': localStorage.getItem('email') || '' },
          body: JSON.stringify({ context: this.context, action, message, chat_id: this.chatId, user_id: this.userId })
        })
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const event = JSON.parse(line.slice(6))
            if (event.type === 'done') {
              if (event.context) this.context = event.context
              this.messages.splice(streamIdx, 1, { role: 'system', content: event.content, timestamp: Date.now(), streaming: false })
              this.saveTurn('bot', this.userId, JSON.stringify(event.content))
              this.emitSummary()
              this.persistState()
            } else if (event.type === 'error') {
              console.error('[nudge-template-chat] server error:', event.msg)
              this.messages.splice(streamIdx, 1, { role: 'system', text: `⚠️ ${event.msg || 'Something went wrong.'}`, timestamp: Date.now(), streaming: false })
            }
          }
        }
      } catch (err) {
        console.error('[nudge-template-chat] request failed:', err)
        this.messages.splice(streamIdx, 1, { role: 'system', text: `⚠️ ${err.message || 'Something went wrong.'}`, timestamp: Date.now(), streaming: false })
      } finally {
        this.loading = false
      }
    },

    emitSummary () {
      this.$emit('summary-change', { step: TEMPLATE_STEP_NUMBER[this.context.step] || 1, summary: this.context.summary || {} })
    }
  }
}
</script>
<style scoped src="../create-campaign/chat-panel/ChatPanel.css"></style>



// WEBPACK FOOTER //
// TemplateChatPanel.vue?6d79a102